import { ActivationAction } from 'src/common/components/Products/actions/types/activationActionsTypes';
import { LastActiveProfile } from '../RSimApi/rSimApi';
import { RsimProfileStatus } from '../RSimApi/rSimApi.interface';
import { TagsData } from 'src/tags/models/tagsApi.types';
import { ACTIVATION_ACTIONS } from 'src/common/components/Products/actions/constants/activationActionsConstants';
import { NormilizedAssignedTags } from 'src/tags/models/tags';
import { SubscriptionStatus } from 'src/common/components/SubscriptionStatus/constants/subscriptionStatus';
import { ActiveProfileFilter, ACTIVE_PROFILE_FILTER } from './activeProfile';
import { LastUpdateTimeFilter, LAST_UPDATE_TIME_FILTERS } from './lastUpdate';
import { RSimIdTypeName, RSIM_PROFILE_ID_NAMES } from './RSimProfile.model';
import { SimulateOutageFilter, SIMULATE_OUTAGE_FILTER } from './simulateOutage';
import { parseResponseDateAndTime } from '@/common/utils/parseResponseDate';

export type CycleToData = {
  dataUsage: number;
  inMinuteUsage: number;
  inTextUsage: number;
  outMinuteUsage: number;
  outTextUsage: number;
};

export type RSimDTO = {
  accountName: string;
  accountRef: number;
  euicc: string;
  lastAckTimestamp: string;
  lastActiveProfile: LastActiveProfile;
  primaryIccid: string;
  rsimProfileStatus: RsimProfileStatus;
  secondaryIccid: string;
  cycleTo: CycleToData;
  appliedOtaConfigurationProfile: string;
  appliedOtaConfigurationVersion: string;
  isPendingConfiguration: boolean;
  isCustomConfigurationDetected: boolean;
  connectionId: string;
  orderNumber: string;
  customerReference: string;
  iccidOta: string;
  activationAction: ActivationAction;
  productCode?: string | null;
  installerRef?: number | null;
  installerName?: string | null;
  subscriptionStatus: SubscriptionStatus | null;
};

export type RSimsDTO = RSimDTO[];

export type RSimLastPollingTimestampDTO = {
  id: string;
  lastPollingTimestamp: string;
};
export type RSimsLastPollingTimestampDTO = RSimLastPollingTimestampDTO[];

type ExternalOptionalData = {
  normalizedTagsData?: NormilizedAssignedTags;
};

export const mapRSimDTO = (rsim: RSimDTO, externalOptionalData?: ExternalOptionalData) => {
  let tags;
  if (externalOptionalData?.normalizedTagsData?.[rsim.euicc]) {
    const { items, hasMore } = externalOptionalData.normalizedTagsData[rsim.euicc];
    tags = {
      items: items,
      hasMore: hasMore,
    } as TagsData;
  }

  return {
    id: rsim.euicc,
    accountName: rsim.accountName || '',
    accountNumber: rsim.accountRef?.toString() || '',
    status: rsim.rsimProfileStatus,
    euicc: rsim.euicc,
    primaryIccid: rsim.primaryIccid,
    secondaryIccid: rsim.secondaryIccid,
    dataUsage: rsim.cycleTo.dataUsage,
    inMinuteUsage: rsim.cycleTo.inMinuteUsage,
    inTextUsage: rsim.cycleTo.inTextUsage,
    outMinuteUsage: rsim.cycleTo.outMinuteUsage,
    outTextUsage: rsim.cycleTo.outTextUsage,
    lastPollingTimestamp: parseResponseDateAndTime(rsim.lastAckTimestamp),
    tags,
    appliedOtaConfigurationProfile: rsim.appliedOtaConfigurationProfile,
    appliedOtaConfigurationVersion: rsim.appliedOtaConfigurationVersion,
    isPendingConfiguration: rsim.isPendingConfiguration,
    isCustomConfigurationDetected: rsim.isCustomConfigurationDetected,
    lastActiveProfile: rsim.lastActiveProfile,
    connectionId: rsim.connectionId,
    orderNumber: rsim.orderNumber,
    customerReference: rsim.customerReference,
    iccidOta: rsim.iccidOta,
    activationAction: rsim.activationAction?.toLowerCase() || ACTIVATION_ACTIONS.NONE,
    productCode: rsim.productCode,
    installerName: rsim.installerName || null,
    installerRef: rsim.installerRef || null,
    subscriptionStatus: rsim.subscriptionStatus,
  };
};

export const mapRSimsDTO = (
  rsimProfiles: RSimsDTO,
  externalOptionalData?: ExternalOptionalData,
) => {
  return rsimProfiles.map((profile) => {
    return mapRSimDTO(profile, externalOptionalData);
  });
};

export type RSimInventoryItem = ReturnType<typeof mapRSimDTO>;

export type RSimsData = {
  items: ReturnType<typeof mapRSimsDTO>;
};
type BlankableSearchFilter<T extends string | number = string> = T | null;

export type RSimFilters = {
  chosenIdType: RSimIdTypeName;
  searchText: string;
  eidFrom: string;
  eidTo: string;
  orderNumber: string;
  connectionId: string;
  simulateOutage: SimulateOutageFilter;
  lastActiveProfile: ActiveProfileFilter;
  lastUpdateTime: LastUpdateTimeFilter;
  tags: string[];
  accounts: BlankableSearchFilter[];
  productCodes: BlankableSearchFilter[];
  subscriptionStatus: SubscriptionStatus[];
  mobileNetworkOperator: BlankableSearchFilter[];
  mobileNetworkOperatorId: BlankableSearchFilter<number>[];
  mnoNames: string[];
};

export type ValidRSimFiltersKeys = keyof RSimFilters;

export const RSIM_INITIAL_FILTERS: RSimFilters = {
  chosenIdType: RSIM_PROFILE_ID_NAMES.EID,
  searchText: '',
  lastActiveProfile: ACTIVE_PROFILE_FILTER.ANY,
  lastUpdateTime: LAST_UPDATE_TIME_FILTERS.NONE,
  tags: [],
  connectionId: '',
  orderNumber: '',
  simulateOutage: SIMULATE_OUTAGE_FILTER.ANY,
  accounts: [],
  productCodes: [],
  eidFrom: '',
  eidTo: '',
  subscriptionStatus: [],
  mobileNetworkOperator: [],
  mobileNetworkOperatorId: [],
  mnoNames: [],
};
